import React, { useState, useEffect, useRef, useCallback } from 'react'
import Plot from 'react-plotly.js'
import './ConcurrentUsers.css'
import { getEnv } from '../config/env'

const POLL_INTERVAL = 3000 // 3 seconds
const SESSION_EXPIRY = 30000 // Remove session from cache if not seen for 30s
const API_BASE = getEnv('REACT_APP_AI_EXTERNAL_API_BACKEND_BASE_URL')

/**
 * Client-side session cache.
 * - On each poll, we receive sessions from one worker.
 * - We merge them into a unified cache keyed by session_key.
 * - If a session hasn't appeared in ANY response for 30s, we expire it.
 */
const buildMergedView = (sessionCache) => {
  const now = Date.now()
  const allSessions = []
  const by_company = {}
  const by_phase = {}
  const workers = new Set()

  Object.entries(sessionCache).forEach(([key, { session, lastSeenByFrontend }]) => {
    if (now - lastSeenByFrontend > SESSION_EXPIRY) return // Expired
    if (session.current_phase === 'Completed') return // Already in completed list
    allSessions.push(session)
    workers.add(session.worker_pid)
    const cc = session.company_code || 'unknown'
    const phase = session.current_phase || 'unknown'
    by_company[cc] = (by_company[cc] || 0) + 1
    by_phase[phase] = (by_phase[phase] || 0) + 1
  })

  return {
    total_active: allSessions.length,
    unique_users: new Set(allSessions.map((s) => s.user_id)).size,
    unique_companies: Object.keys(by_company).length,
    by_company,
    by_phase,
    active_workers: [...workers],
    sessions: allSessions,
  }
}

const ConcurrentUsers = () => {
  const [capacityData, setCapacityData] = useState(null)
  const [mergedSessions, setMergedSessions] = useState(null)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [phaseFilter, setPhaseFilter] = useState('')
  const [completedSessions, setCompletedSessions] = useState([])
  const [revision, setRevision] = useState(0)
  const sessionCacheRef = useRef({}) // { session_key: { session, lastSeenByFrontend } }
  const completedSetRef = useRef(new Set()) // track session_keys already counted
  const intervalRef = useRef(null)

  const clearCompleted = useCallback(() => {
    setCompletedSessions([])
    completedSetRef.current = new Set()
  }, [])

  const fetchAll = useCallback(async () => {
    try {
      // Fire multiple session polls in parallel to hit different workers
      const workerCount = capacityData?.workers || 4
      const sessionPolls = Array.from({ length: workerCount }, () =>
        window.PDR.api.get(`${API_BASE}/concurrency/sessions`)
      )

      const [capacityResp, ...sessionResps] = await Promise.all([
        window.PDR.api.get(`${API_BASE}/concurrency`),
        ...sessionPolls,
      ])
      setCapacityData(capacityResp)

      // Merge sessions from all poll responses (dedup by session_key)
      const now = Date.now()
      sessionResps.forEach((resp) => {
        const incomingSessions = resp.sessions || []
        incomingSessions.forEach((s) => {
          sessionCacheRef.current[s.session_key] = {
            session: s,
            lastSeenByFrontend: now,
          }
        })
      })

      // Clean expired entries from cache — move them to completed list
      const newCompleted = []
      Object.keys(sessionCacheRef.current).forEach((key) => {
        const entry = sessionCacheRef.current[key]
        const isExpired = now - entry.lastSeenByFrontend > SESSION_EXPIRY
        const isCompleted = entry.session.current_phase === 'Completed'
        // Only count sessions that explicitly reached "Completed" phase
        const isConversation = entry.session.endpoint === '/conversation' && entry.session.method === 'POST'
        if (isCompleted && isConversation && !completedSetRef.current.has(key)) {
          completedSetRef.current.add(key)
          newCompleted.push({
            ...entry.session,
            completed_at: new Date().toLocaleTimeString(),
          })
        }
        if (isExpired) {
          delete sessionCacheRef.current[key]
        }
      })

      if (newCompleted.length > 0) {
        setCompletedSessions((prev) => [...prev, ...newCompleted])
      }

      setMergedSessions(buildMergedView(sessionCacheRef.current))
      setLastUpdated(new Date())
      setRevision((r) => r + 1)
      setError(null)
    } catch (err) {
      console.error('Dashboard fetch error:', err)
      setError('Failed to fetch concurrency data')
    }
  }, [])

  useEffect(() => {
    fetchAll()
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL)
    return () => clearInterval(intervalRef.current)
  }, [fetchAll])

  const getUtilizationColor = (pct) => {
    if (pct >= 90) return '#ef4444'
    if (pct >= 75) return '#f59e0b'
    return '#10b981'
  }

  // Fixed phase order and colors so pie slices never jump around
  const PHASE_CONFIG = [
    { name: 'initializing', emoji: '🚀', color: '#94a3b8' },
    { name: 'Thinking', emoji: '🧠', color: '#8b5cf6' },
    { name: 'Running tool', emoji: '🔧', color: '#f59e0b' },
    { name: 'Searching knowledge base', emoji: '🔍', color: '#3b82f6' },
    { name: 'Calling agent', emoji: '🤖', color: '#6366f1' },
    { name: 'Reading from agent', emoji: '🤖', color: '#818cf8' },
    { name: 'Processing file', emoji: '📄', color: '#14b8a6' },
    { name: 'Reading view data', emoji: '📊', color: '#06b6d4' },
    { name: 'Reading spreadsheet', emoji: '📑', color: '#0ea5e9' },
    { name: 'Analyzing results', emoji: '📈', color: '#10b981' },
    { name: 'Processing', emoji: '⚙️', color: '#64748b' },
    { name: 'Creating objects', emoji: '🏗️', color: '#d946ef' },
    { name: 'Activating skill', emoji: '⚡', color: '#eab308' },
    { name: 'Waiting for approval', emoji: '⏳', color: '#f97316' },
    { name: 'Generating response', emoji: '💬', color: '#22c55e' },
    { name: 'Completed', emoji: '✅', color: '#059669' },
  ]
  const PHASE_ORDER = PHASE_CONFIG.map((p) => p.name)
  const PHASE_COLOR_MAP = Object.fromEntries(PHASE_CONFIG.map((p) => [p.name, p.color]))
  const PHASE_EMOJI_MAP = Object.fromEntries(PHASE_CONFIG.map((p) => [p.name, p.emoji]))

  const getPhaseEmoji = (phase) => PHASE_EMOJI_MAP[phase] || '⏱️'

  // Build stable pie data: always same order, only include phases with count > 0
  const getStablePieData = (byPhase) => {
    if (!byPhase) return { labels: [], values: [], colors: [] }
    // Include known phases in fixed order, then any unknown phases at the end
    const knownLabels = []
    const knownValues = []
    const knownColors = []
    PHASE_ORDER.forEach((phase) => {
      if (byPhase[phase]) {
        knownLabels.push(phase)
        knownValues.push(byPhase[phase])
        knownColors.push(PHASE_COLOR_MAP[phase])
      }
    })
    // Any phases not in our config (future-proof)
    Object.keys(byPhase).forEach((phase) => {
      if (!PHASE_ORDER.includes(phase)) {
        knownLabels.push(phase)
        knownValues.push(byPhase[phase])
        knownColors.push('#cbd5e1')
      }
    })
    return { labels: knownLabels, values: knownValues, colors: knownColors }
  }

  const filteredSessions = (mergedSessions?.sessions || []).filter((s) => {
    const matchesSearch =
      !searchTerm ||
      s.company_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesPhase = !phaseFilter || s.current_phase === phaseFilter
    return matchesSearch && matchesPhase
  })

  return (
    <div className="concurrent-dashboard">
      <header className="dashboard-header">
        <h1>🖥️ Concurrent Users Dashboard</h1>
        <div className="header-meta">
          {lastUpdated && <span className="last-updated">Last updated: {lastUpdated.toLocaleTimeString()}</span>}
          {mergedSessions?.active_workers && (
            <span className="workers-badge">{mergedSessions.active_workers.length} worker{mergedSessions.active_workers.length !== 1 ? 's' : ''}</span>
          )}
          <span className={`status-dot ${error ? 'error' : 'ok'}`} />
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      {/* Summary Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-value">{mergedSessions?.total_active ?? '—'}</div>
          <div className="stat-label">Active Requests</div>
        </div>
        <div className="stat-card highlight-green">
          <div className="stat-value">{completedSessions.length}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{mergedSessions?.unique_users ?? '—'}</div>
          <div className="stat-label">Unique Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{mergedSessions?.unique_companies ?? '—'}</div>
          <div className="stat-label">Company Codes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {mergedSessions?.sessions?.length
              ? (mergedSessions.sessions.reduce((a, s) => a + (s.duration_s || 0), 0) / mergedSessions.sessions.length).toFixed(1) + 's'
              : '—'}
          </div>
          <div className="stat-label">Avg Duration</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{capacityData?.max_capacity ?? '—'}</div>
          <div className="stat-label">Max Capacity</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Capacity Gauge */}
        <div className="chart-card">
          <h3>Capacity Utilization</h3>
          {capacityData && (
            <Plot
              data={[
                {
                  type: 'indicator',
                  mode: 'gauge+number',
                  value: capacityData.max_capacity
                    ? Math.round(((mergedSessions?.total_active || 0) / capacityData.max_capacity) * 1000) / 10
                    : 0,
                  title: { text: 'Utilization % (all workers)' },
                  gauge: {
                    axis: { range: [0, 100] },
                    bar: {
                      color: getUtilizationColor(
                        capacityData.max_capacity
                          ? ((mergedSessions?.total_active || 0) / capacityData.max_capacity) * 100
                          : 0
                      ),
                    },
                    steps: [
                      { range: [0, 75], color: '#ecfdf5' },
                      { range: [75, 90], color: '#fef3c7' },
                      { range: [90, 100], color: '#fef2f2' },
                    ],
                    threshold: {
                      line: { color: 'red', width: 4 },
                      thickness: 0.75,
                      value: 90,
                    },
                  },
                },
              ]}
              layout={{
                width: 350,
                height: 280,
                margin: { t: 40, b: 0, l: 30, r: 30 },
                paper_bgcolor: 'transparent',
                font: { color: '#475569' },
                transition: { duration: 500, easing: 'cubic-in-out' },
                uirevision: 'gauge',
              }}
              config={{ displayModeBar: false }}
              revision={revision}
            />
          )}
          {capacityData && (
            <div className="gauge-details">
              <span>
                {mergedSessions?.total_active || 0} / {capacityData.max_capacity} threads
              </span>
              <span>Workers: {capacityData.workers}</span>
            </div>
          )}
        </div>

        {/* Company Code Bar Chart */}
        <div className="chart-card">
          <h3>Active Users by Company</h3>
          {mergedSessions?.by_company && Object.keys(mergedSessions.by_company).length > 0 ? (
            <Plot
              data={[
                {
                  type: 'bar',
                  orientation: 'h',
                  y: Object.keys(mergedSessions.by_company),
                  x: Object.values(mergedSessions.by_company),
                  marker: {
                    color: Object.values(mergedSessions.by_company).map(
                      (_, i) => `hsl(${(i * 47) % 360}, 70%, 55%)`
                    ),
                  },
                  text: Object.values(mergedSessions.by_company).map(String),
                  textposition: 'outside',
                },
              ]}
              layout={{
                width: 400,
                height: 280,
                margin: { t: 10, b: 30, l: 100, r: 40 },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: { color: '#475569' },
                xaxis: { showgrid: true, gridcolor: '#e2e8f0' },
                yaxis: { automargin: true },
                transition: { duration: 500, easing: 'cubic-in-out' },
                uirevision: 'company',
              }}
              config={{ displayModeBar: false }}
              revision={revision}
            />
          ) : (
            <div className="no-data">No active sessions</div>
          )}
        </div>

        {/* Phase Distribution */}
        <div className="chart-card">
          <h3>Current Phases</h3>
          {mergedSessions?.by_phase && Object.keys(mergedSessions.by_phase).length > 0 ? (
            (() => {
              const pieData = getStablePieData(mergedSessions.by_phase)
              return (
                <Plot
                  data={[
                    {
                      type: 'pie',
                      labels: pieData.labels,
                      values: pieData.values,
                      hole: 0.4,
                      textinfo: 'value',
                      textposition: 'inside',
                      sort: false,
                      direction: 'clockwise',
                      marker: { colors: pieData.colors },
                    },
                  ]}
                  layout={{
                    width: 380,
                    height: 280,
                    margin: { t: 10, b: 10, l: 10, r: 10 },
                    paper_bgcolor: 'transparent',
                    font: { color: '#475569', size: 10 },
                    showlegend: false,
                    transition: { duration: 500, easing: 'cubic-in-out' },
                    uirevision: 'phase',
                  }}
                  config={{ displayModeBar: false }}
                  revision={revision}
                />
              )
            })()
          ) : (
            <div className="no-data">No active sessions</div>
          )}
          {mergedSessions?.by_phase && Object.keys(mergedSessions.by_phase).length > 0 && (
            <div className="phase-legend">
              {getStablePieData(mergedSessions.by_phase).labels.map((label, i) => (
                <div key={label} className="phase-legend-item">
                  <span className="phase-legend-dot" style={{ background: getStablePieData(mergedSessions.by_phase).colors[i] }} />
                  <span className="phase-legend-label">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Live Sessions Table */}
      <div className="sessions-section">
        <div className="sessions-header">
          <h3>🔴 Live Sessions</h3>
          <div className="sessions-filters">
            <input
              type="text"
              placeholder="Search company/user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input"
            />
            <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} className="filter-select">
              <option value="">All phases</option>
              {mergedSessions?.by_phase &&
                Object.keys(mergedSessions.by_phase).map((phase) => (
                  <option key={phase} value={phase}>
                    {phase} ({mergedSessions.by_phase[phase]})
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="sessions-table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>User</th>
                <th>Endpoint</th>
                <th>Phase</th>
                <th>Detail</th>
                <th>Duration</th>
                <th>Worker</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-sessions">
                    No active sessions
                  </td>
                </tr>
              ) : (
                filteredSessions
                  .sort((a, b) => (b.duration_s || 0) - (a.duration_s || 0))
                  .map((session) => (
                    <tr key={session.request_id} className={session.duration_s > 30 ? 'long-running' : ''}>
                      <td className="company-cell">{session.company_code}</td>
                      <td className="user-cell" title={session.user_id}>
                        {session.user_id?.split('@')[0] || session.user_id}
                      </td>
                      <td className="endpoint-cell">{session.endpoint}</td>
                      <td className="phase-cell">
                        <span className="phase-badge">
                          {getPhaseEmoji(session.current_phase)} {session.current_phase}
                        </span>
                      </td>
                      <td className="detail-cell">{session.phase_detail || '—'}</td>
                      <td className="duration-cell">
                        <span className={`duration ${session.duration_s > 30 ? 'warning' : ''}`}>
                          {session.duration_s?.toFixed(1)}s
                        </span>
                      </td>
                      <td className="worker-cell">{session.worker_pid}</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Completed Requests */}
      <div className="sessions-section completed-section">
        <div className="sessions-header">
          <h3>✅ Completed Requests ({completedSessions.length})</h3>
          <button className="clear-btn" onClick={clearCompleted}>Clear</button>
        </div>
        {completedSessions.length > 0 && (
          <div className="sessions-table-container" style={{ maxHeight: '250px' }}>
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>User</th>
                  <th>Endpoint</th>
                  <th>Duration</th>
                  <th>Completed At</th>
                  <th>Worker</th>
                </tr>
              </thead>
              <tbody>
                {[...completedSessions].reverse().map((session, i) => (
                  <tr key={`${session.session_key}-${i}`}>
                    <td className="company-cell">{session.company_code}</td>
                    <td className="user-cell" title={session.user_id}>
                      {session.user_id?.split('@')[0] || session.user_id}
                    </td>
                    <td className="endpoint-cell">{session.endpoint}</td>
                    <td className="duration-cell">{session.duration_s?.toFixed(1)}s</td>
                    <td>{session.completed_at}</td>
                    <td className="worker-cell">{session.worker_pid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alerts */}
      {capacityData && mergedSessions && capacityData.max_capacity > 0 && (
        ((mergedSessions.total_active / capacityData.max_capacity) * 100) >= 75
      ) && (
        <div className={`alert-banner ${((mergedSessions.total_active / capacityData.max_capacity) * 100) >= 90 ? 'critical' : 'warning'}`}>
          ⚠️ {((mergedSessions.total_active / capacityData.max_capacity) * 100) >= 90 ? 'CRITICAL' : 'HIGH'} utilization:{' '}
          {Math.round((mergedSessions.total_active / capacityData.max_capacity) * 100)}% — {mergedSessions.total_active}/{capacityData.max_capacity}{' '}
          threads in use
        </div>
      )}
    </div>
  )
}

export default ConcurrentUsers
